import { computed, inject, Injectable } from '@angular/core';

import { ClassStore } from '../../stores/class.store';
import { UserRole } from '../../stores/user.store';
import { isMyEldProductCode } from '../product-variant/product-variant.service';
import { VisibleTabsContext } from './product-config.types';

export type ProdType = 'ilit' | 'myeld' | 'wtw';

/**
 * Every tab the bottom-nav can render, across both roles. `CvBottomNavTabId`
 * re-exports this so components need not reach into `core/services`.
 */
export type TabKey =
  | 'planner'
  | 'lessons'
  | 'assignments'
  | 'performance'
  | 'library'
  | 'groups'
  | 'organizer'
  | 'review'
  | 'notebook'
  | 'connect';

export const TEACHER_TAB_ORDER: ReadonlyArray<TabKey> = [
  'planner',
  'lessons',
  'assignments',
  'performance',
  'library',
  'groups',
];

/**
 * iLit20 teacher tab order — Organizer / Performance / Library / Groups.
 */
export const ILIT20_TEACHER_TAB_ORDER: ReadonlyArray<TabKey> = [
  'organizer',
  'performance',
  'library',
  'groups',
];

export const STUDENT_TAB_ORDER: ReadonlyArray<TabKey> = [
  'review',
  'library',
  'notebook',
  'assignments',
  'connect',
];

/**
 * Used by `cv-app-shell` to check a URL segment names a real tab, without
 * duplicating the id list.
 */
export const ALL_TAB_KEYS: ReadonlyArray<TabKey> = Array.from(
  new Set<TabKey>([...TEACHER_TAB_ORDER, ...ILIT20_TEACHER_TAB_ORDER, ...STUDENT_TAB_ORDER]),
);

@Injectable({ providedIn: 'root' })
export class ProductConfigService {
  private readonly classStore = inject(ClassStore);

  readonly prodType = computed<ProdType>(() =>
    isMyEldProductCode(this.classStore.productCode()) ? 'myeld' : 'ilit',
  );

  readonly themeClass = computed(() => `app-${this.prodType()}`);

  /**
   * Which tabs to show, and in what order. Pure: everything comes from the
   * arguments, so callers stay in control of what drives it.
   */
  visibleTabs(role: UserRole | null, ctx: VisibleTabsContext): ReadonlyArray<TabKey> {
    if (role === 'student') return STUDENT_TAB_ORDER;
    const tabs = ctx.isIlit20 ? ILIT20_TEACHER_TAB_ORDER : TEACHER_TAB_ORDER;
    if (!ctx.isCoTeacher) return tabs;
    return tabs.filter((id) => id !== 'groups');
  }
}
